import { useEffect, useMemo, useRef, useState } from "react";
import { berechneAngebot, type PositionInput } from "../../shared/calc.js";
import {
  formatCent,
  formatE4,
  formatProzent,
  formatTonnen,
  parseEuroToE4,
  parseTonnenToKg,
} from "../../shared/money.js";
import {
  LIEFERART_TEXT,
  RHYTHMUS_TEXT,
  berechneAbrufplan,
  pruefeKontrakt,
  type Lieferart,
  type Rhythmus,
  type Vertragsart,
} from "../../shared/kontrakt.js";
import type { AngebotDto, PositionDto } from "../../shared/types.js";
import { bauePositionInput, pruefePosition } from "../../shared/positionen.js";
import { api } from "../api.js";
import { useStammdaten } from "../StammdatenContext.js";

const heute = () => new Date().toISOString().slice(0, 10);
const inTagen = (n: number) =>
  new Date(Date.now() + n * 86_400_000).toISOString().slice(0, 10);

const leerePosition = (): PositionDto & { mengeText: string } => ({
  product_id: null,
  packaging_id: null,
  bezeichnung: "",
  menge_kg: 0,
  mengeText: "",
  ca_menge: true,
  surcharge_ids: [],
  rabatt_bp: 0,
});

export function Kalkulation({
  uebernahme,
  beiGespeichert,
}: {
  /** Positionen aus der Schnellkalkulation, einmalig beim Mount übernommen. */
  uebernahme?: PositionDto[] | null;
  beiGespeichert?: () => void;
} = {}) {
  const { stamm, fehler: ladeFehler } = useStammdaten();
  const [fehler, setFehler] = useState<string | null>(null);
  const [erfolg, setErfolg] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);

  const [kundeId, setKundeId] = useState("");
  const [kundeFirma, setKundeFirma] = useState("");
  const [kundeStrasse, setKundeStrasse] = useState("");
  const [kundePlz, setKundePlz] = useState("");
  const [kundeOrt, setKundeOrt] = useState("");
  const [ansprechpartner, setAnsprechpartner] = useState("");

  const [angebotsdatum, setAngebotsdatum] = useState(heute);
  const [bindefrist, setBindefrist] = useState(() => inTagen(14));
  const [verbindlichkeit, setVerbindlichkeit] =
    useState<"verbindlich" | "freibleibend">("freibleibend");
  const [lieferbedingung, setLieferbedingung] =
    useState<"ab_werk" | "frachtfrei_empfaenger">("frachtfrei_empfaenger");
  const [frachtzone, setFrachtzone] = useState("");
  const [zahlungsziel, setZahlungsziel] = useState(14);

  const [vertragsart, setVertragsart] = useState<Vertragsart>("spot");
  const [lieferart, setLieferart] = useState<Lieferart>("prompt");
  const [kontraktVon, setKontraktVon] = useState("");
  const [kontraktBis, setKontraktBis] = useState("");
  const [rhythmus, setRhythmus] = useState<Rhythmus>("monatlich");
  const [notierungsbezug, setNotierungsbezug] = useState("");
  const [notierungskurs, setNotierungskurs] = useState("");
  const [notierungsdatum, setNotierungsdatum] = useState("");
  const [bemerkung, setBemerkung] = useState("");

  const [positionen, setPositionen] = useState([leerePosition()]);

  // Übergabe aus der Schnellkalkulation. Nur einmal — sonst kämen dieselben
  // Positionen bei jedem erneuten Öffnen wieder dazu.
  const uebernommen = useRef(false);
  useEffect(() => {
    if (!uebernahme?.length || uebernommen.current) return;
    uebernommen.current = true;
    setPositionen(
      uebernahme.map((p) => ({
        ...p,
        mengeText: String(p.menge_kg / 1000).replace(".", ","),
      })),
    );
  }, [uebernahme]);

  function waehleKunde(id: string) {
    setKundeId(id);
    const k = stamm?.kunden.find((k) => k.id === id);
    if (k) {
      setKundeFirma(k.firma);
      setKundeStrasse(k.strasse ?? "");
      setKundePlz(k.plz ?? "");
      setKundeOrt(k.ort ?? "");
      setAnsprechpartner(k.ansprechpartner ?? "");
      setZahlungsziel(k.zahlungsziel_tage);
    }
  }

  function aendere(i: number, teil: Partial<(typeof positionen)[number]>) {
    setPositionen((alt) => alt.map((p, j) => (i === j ? { ...p, ...teil } : p)));
  }

  /**
   * Live-Vorschau mit exakt derselben Funktion, die der Worker beim Speichern
   * benutzt. Was hier steht, kommt beim Speichern nochmal serverseitig heraus.
   */
  const vorschau = useMemo(() => {
    if (!stamm) return null;

    const frachtE4 =
      lieferbedingung === "ab_werk"
        ? (stamm.frachtzonen.find((z) => z.id === frachtzone)?.abschlag_e4 ?? 0)
        : 0;

    const eingaben: PositionInput[] = [];
    for (const p of positionen) {
      const produkt = stamm.produkte.find((x) => x.id === p.product_id);
      if (!produkt || !p.menge_kg) continue;
      const verpackung = stamm.verpackungen.find((v) => v.id === p.packaging_id);

      eingaben.push(
        bauePositionInput({
          produkt,
          verpackung: verpackung ?? null,
          zuschlaege: p.surcharge_ids
            .map((id) => stamm.zuschlaege.find((z) => z.id === id))
            .filter((z): z is NonNullable<typeof z> => !!z)
            .map((z) => ({ id: z.id, bezeichnung: z.bezeichnung, betrag_e4: z.betrag_e4 })),
          frachtE4,
          mengeKg: p.menge_kg,
          rabattBp: p.rabatt_bp,
        }),
      );
    }

    return eingaben.length ? { ergebnis: berechneAngebot(eingaben), eingaben } : null;
  }, [stamm, positionen, lieferbedingung, frachtzone]);

  /**
   * Abrufplan aus Kontraktzeitraum und Summe der Positionsmengen. Die
   * Kontraktmenge wird bewusst nicht separat erfasst — sonst könnten
   * Positionssumme und Kontraktmenge auseinanderlaufen.
   */
  const kontraktMengeKg = positionen.reduce((s, p) => s + (p.menge_kg || 0), 0);
  const kontraktFehler =
    vertragsart === "kontrakt"
      ? pruefeKontrakt(kontraktVon, kontraktBis, kontraktMengeKg, angebotsdatum)
      : null;
  const abrufplan =
    vertragsart === "kontrakt" && !kontraktFehler
      ? berechneAbrufplan(kontraktVon, kontraktBis, kontraktMengeKg, rhythmus)
      : null;

  /**
   * Alles zurücksetzen, nicht nur die Positionen.
   *
   * Solange die Maske auf einem eigenen Reiter lag, hat der Reiterwechsel
   * das verdeckt. Eingebettet neben der Angebotsliste würde das nächste
   * Angebot sonst still Kunde, Kontraktzeitraum und Bemerkung erben. Datum
   * und Bindefrist werden neu berechnet — sie stammen aus dem Mount und
   * veralten in einer dauerhaft geöffneten Maske über Nacht.
   */
  function zuruecksetzen() {
    setKundeId("");
    setKundeFirma("");
    setKundeStrasse("");
    setKundePlz("");
    setKundeOrt("");
    setAnsprechpartner("");
    setAngebotsdatum(heute());
    setBindefrist(inTagen(14));
    setVerbindlichkeit("freibleibend");
    setLieferbedingung("frachtfrei_empfaenger");
    setFrachtzone("");
    setZahlungsziel(14);
    setBemerkung("");
    setVertragsart("spot");
    setLieferart("prompt");
    setKontraktVon("");
    setKontraktBis("");
    setRhythmus("monatlich");
    setNotierungsbezug("");
    setNotierungskurs("");
    setNotierungsdatum("");
    setPositionen([leerePosition()]);
  }

  async function speichern() {
    setFehler(null);
    setErfolg(null);
    setSpeichert(true);
    try {
      const dto: AngebotDto = {
        customer_id: kundeId || null,
        kunde_firma: kundeFirma,
        kunde_strasse: kundeStrasse || null,
        kunde_plz: kundePlz || null,
        kunde_ort: kundeOrt || null,
        kunde_ust_id: null,
        ansprechpartner: ansprechpartner || null,
        angebotsdatum,
        bindefrist_bis: bindefrist || null,
        verbindlichkeit,
        lieferbedingung,
        freight_zone_id: lieferbedingung === "ab_werk" ? frachtzone || null : null,
        zahlungsziel_tage: zahlungsziel,
        vertragsart,
        lieferart: vertragsart === "kontrakt" ? "termin" : lieferart,
        kontrakt_von: vertragsart === "kontrakt" ? kontraktVon : null,
        kontrakt_bis: vertragsart === "kontrakt" ? kontraktBis : null,
        abruf_rhythmus: vertragsart === "kontrakt" ? rhythmus : null,
        teilpartie_min_kg: null,
        notierungsbezug: notierungsbezug || null,
        notierungskurs_e4: notierungskurs ? parseEuroToE4(notierungskurs) : null,
        notierungsdatum: notierungsdatum || null,
        bemerkung: bemerkung || null,
        positionen: positionen
          .filter((p) => p.product_id && p.menge_kg > 0)
          .map(({ mengeText: _mengeText, ...p }) => p),
      };

      const antwort = await api.post<{ angebotsnr: string }>("/angebote", dto);
      setErfolg(`Angebot ${antwort.angebotsnr} wurde gespeichert.`);
      zuruecksetzen();
      beiGespeichert?.();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
    } finally {
      setSpeichert(false);
    }
  }

  if (ladeFehler && !stamm)
    return (
      <div className="meldung fehler" role="alert">
        {ladeFehler}
      </div>
    );
  if (!stamm) return <p className="leer">Stammdaten werden geladen …</p>;

  // Dieselbe Prüfung, die der Worker beim Speichern durchsetzt — sonst
  // läuft der Nutzer statt in einen gesperrten Knopf in einen Serverfehler.
  const positionsFehler = positionen
    .filter((p) => p.product_id)
    .map((p) =>
      pruefePosition({
        produkt: stamm.produkte.find((x) => x.id === p.product_id) ?? null,
        verpackung: stamm.verpackungen.find((v) => v.id === p.packaging_id) ?? null,
        mengeKg: p.menge_kg,
        rabattBp: p.rabatt_bp,
      }),
    )
    .filter((m): m is string => !!m);

  const kannSpeichern =
    !!kundeFirma.trim() &&
    !!vorschau &&
    vorschau.ergebnis.positionen.length > 0 &&
    !kontraktFehler &&
    !positionsFehler.length;

  return (
    <>
      {fehler && (
        <div className="meldung fehler" role="alert">
          {fehler}
        </div>
      )}
      {erfolg && (
        <div className="meldung erfolg" role="status">
          {erfolg}
        </div>
      )}

      <div className="spalten">
        <div>
          {/* ── Kunde ─────────────────────────────────────────────────── */}
          <section className="karte">
            <h2>Kunde</h2>
            <div className="feld">
              <label htmlFor="kunde">Aus Stammdaten übernehmen</label>
              <select id="kunde" value={kundeId} onChange={(e) => waehleKunde(e.target.value)}>
                <option value="">— freie Eingabe —</option>
                {stamm.kunden.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.kundennr} · {k.firma}
                  </option>
                ))}
              </select>
            </div>

            <div className="feld">
              <label htmlFor="firma">Firma</label>
              <input
                id="firma"
                value={kundeFirma}
                onChange={(e) => setKundeFirma(e.target.value)}
                required
              />
            </div>

            <div className="raster">
              <div className="feld">
                <label htmlFor="strasse">Straße</label>
                <input
                  id="strasse"
                  value={kundeStrasse}
                  onChange={(e) => setKundeStrasse(e.target.value)}
                />
              </div>
              <div className="feld">
                <label htmlFor="plz">PLZ</label>
                <input id="plz" value={kundePlz} onChange={(e) => setKundePlz(e.target.value)} />
              </div>
              <div className="feld">
                <label htmlFor="ort">Ort</label>
                <input id="ort" value={kundeOrt} onChange={(e) => setKundeOrt(e.target.value)} />
              </div>
              <div className="feld">
                <label htmlFor="ap">Ansprechpartner</label>
                <input
                  id="ap"
                  value={ansprechpartner}
                  onChange={(e) => setAnsprechpartner(e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* ── Konditionen ───────────────────────────────────────────── */}
          <section className="karte">
            <h2>Konditionen</h2>
            <div className="raster">
              <div className="feld">
                <label htmlFor="datum">Angebotsdatum</label>
                <input
                  id="datum"
                  type="date"
                  value={angebotsdatum}
                  onChange={(e) => setAngebotsdatum(e.target.value)}
                />
              </div>
              <div className="feld">
                <label htmlFor="frist">Bindefrist bis</label>
                <input
                  id="frist"
                  type="date"
                  value={bindefrist}
                  onChange={(e) => setBindefrist(e.target.value)}
                />
              </div>
              <div className="feld">
                <label htmlFor="verb">Verbindlichkeit</label>
                <select
                  id="verb"
                  value={verbindlichkeit}
                  onChange={(e) => setVerbindlichkeit(e.target.value as typeof verbindlichkeit)}
                >
                  <option value="freibleibend">freibleibend</option>
                  <option value="verbindlich">verbindlich</option>
                </select>
              </div>
              <div className="feld">
                <label htmlFor="zz">Zahlungsziel (Tage)</label>
                <input
                  id="zz"
                  type="number"
                  min={0}
                  max={90}
                  inputMode="decimal"
                  value={zahlungsziel}
                  onChange={(e) => setZahlungsziel(Number(e.target.value) || 0)}
                />
              </div>
            </div>

            <div className="raster">
              <div className="feld">
                <label htmlFor="lb">Lieferbedingung</label>
                <select
                  id="lb"
                  value={lieferbedingung}
                  onChange={(e) => setLieferbedingung(e.target.value as typeof lieferbedingung)}
                >
                  <option value="frachtfrei_empfaenger">frachtfrei Käuferstation</option>
                  <option value="ab_werk">ab Werk (Selbstabholung)</option>
                </select>
              </div>
              {lieferbedingung === "ab_werk" && (
                <div className="feld">
                  <label htmlFor="fz">Frachtzone (Gutschrift)</label>
                  <select id="fz" value={frachtzone} onChange={(e) => setFrachtzone(e.target.value)}>
                    <option value="">— keine —</option>
                    {stamm.frachtzonen.map((z) => (
                      <option key={z.id} value={z.id}>
                        {z.bezeichnung} ({formatE4(z.abschlag_e4)} €/100 kg)
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            <p className="hinweistext">
              Preise verstehen sich je 100 kg netto, einschließlich Papiersack, frachtfrei
              Käuferstation (Lieferungs- und Zahlungsbedingungen für Mühlenprodukte, Abschn. III).
              Bei Selbstabholung wird die enthaltene Fracht gutgeschrieben.
            </p>
          </section>

          {/* ── Vertragsart ───────────────────────────────────────────── */}
          <section className="karte">
            <h2>Vertragsart</h2>

            <div className="raster">
              <div className="feld">
                <label htmlFor="va">Geschäftsart</label>
                <select
                  id="va"
                  value={vertragsart}
                  onChange={(e) => setVertragsart(e.target.value as Vertragsart)}
                >
                  <option value="spot">Einzelgeschäft (Spot)</option>
                  <option value="kontrakt">Kontrakt über einen Zeitraum</option>
                </select>
              </div>

              {vertragsart === "spot" && (
                <div className="feld">
                  <label htmlFor="la">Lieferzeit</label>
                  <select
                    id="la"
                    value={lieferart}
                    onChange={(e) => setLieferart(e.target.value as Lieferart)}
                  >
                    <option value="sofort">{LIEFERART_TEXT.sofort}</option>
                    <option value="prompt">{LIEFERART_TEXT.prompt}</option>
                  </select>
                </div>
              )}

              {vertragsart === "kontrakt" && (
                <>
                  <div className="feld">
                    <label htmlFor="kv">Kontrakt von</label>
                    <input
                      id="kv"
                      type="date"
                      value={kontraktVon}
                      onChange={(e) => setKontraktVon(e.target.value)}
                    />
                  </div>
                  <div className="feld">
                    <label htmlFor="kb">Kontrakt bis</label>
                    <input
                      id="kb"
                      type="date"
                      value={kontraktBis}
                      onChange={(e) => setKontraktBis(e.target.value)}
                    />
                  </div>
                  <div className="feld">
                    <label htmlFor="kr">Abruf</label>
                    <select
                      id="kr"
                      value={rhythmus}
                      onChange={(e) => setRhythmus(e.target.value as Rhythmus)}
                    >
                      <option value="monatlich">{RHYTHMUS_TEXT.monatlich}</option>
                      <option value="quartalsweise">{RHYTHMUS_TEXT.quartalsweise}</option>
                      <option value="auf_abruf">{RHYTHMUS_TEXT.auf_abruf}</option>
                    </select>
                  </div>
                </>
              )}
            </div>

            {vertragsart === "kontrakt" && (
              <>
                <p className="hinweistext">
                  Ein Kontrakt ist Lieferung auf Termin. Die Kontraktmenge ergibt sich aus den
                  Positionen unten — derzeit{" "}
                  <strong>{formatTonnen(kontraktMengeKg)} t</strong>. Der Preis gilt für die
                  gesamte Laufzeit.
                </p>

                {kontraktFehler && (
                  <div className="meldung fehler" role="alert">
                    {kontraktFehler}
                  </div>
                )}

                {abrufplan && abrufplan.zeilen.length > 0 && (
                  <div className="abrufplan">
                    <div className="abrufplan-zeile kopf">
                      <span>Abrufplan</span>
                      <span>{abrufplan.zeilen.length} Raten</span>
                    </div>
                    {abrufplan.zeilen.map((z) => (
                      <div className="abrufplan-zeile" key={z.bezeichnung}>
                        <span style={{ color: "var(--muted)" }}>{z.bezeichnung}</span>
                        <span>{formatTonnen(z.menge_kg)} t</span>
                      </div>
                    ))}
                  </div>
                )}

                {abrufplan?.hinweise.map((h) => (
                  <div className="meldung hinweis" key={h}>
                    {h}
                  </div>
                ))}

                <div className="raster" style={{ marginTop: "0.9rem" }}>
                  <div className="feld">
                    <label htmlFor="nb">Notierungsbezug</label>
                    <input
                      id="nb"
                      placeholder="z. B. MATIF Weizen Sep 26"
                      value={notierungsbezug}
                      onChange={(e) => setNotierungsbezug(e.target.value)}
                    />
                  </div>
                  <div className="feld">
                    <label htmlFor="nk">Kurs €/t</label>
                    <input
                      id="nk"
                      inputMode="decimal"
                      placeholder="228,50"
                      value={notierungskurs}
                      onChange={(e) => setNotierungskurs(e.target.value)}
                    />
                  </div>
                  <div className="feld">
                    <label htmlFor="nd">Kursdatum</label>
                    <input
                      id="nd"
                      type="date"
                      value={notierungsdatum}
                      onChange={(e) => setNotierungsdatum(e.target.value)}
                    />
                  </div>
                </div>
                <p className="hinweistext">
                  Der Notierungsbezug wird nur auf dem Angebot dokumentiert, nicht in den Preis
                  eingerechnet. Ob Engelke Mehlkontrakte überhaupt an die MATIF bindet, ist
                  offen — siehe PLAN.md.
                </p>
              </>
            )}
          </section>

          {/* ── Positionen ────────────────────────────────────────────── */}
          <section className="karte">
            <h2>Positionen</h2>

            {positionen.map((p, i) => {
              const produkt = stamm.produkte.find((x) => x.id === p.product_id);
              const verpackung = stamm.verpackungen.find((v) => v.id === p.packaging_id);
              const gebindeFehler =
                verpackung?.gebinde_kg && p.menge_kg > 0 && p.menge_kg % verpackung.gebinde_kg !== 0;

              return (
                <div className="position" key={i}>
                  <div className="pos-kopf">
                    <span className="pos-nr">Position {i + 1}</span>
                    {positionen.length > 1 && (
                      <button
                        className="knopf schlicht"
                        onClick={() => setPositionen((a) => a.filter((_, j) => j !== i))}
                      >
                        Entfernen
                      </button>
                    )}
                  </div>

                  <div className="raster">
                    <div className="feld" style={{ gridColumn: "span 2" }}>
                      <label>Artikel</label>
                      <select
                        value={p.product_id ?? ""}
                        onChange={(e) => aendere(i, { product_id: e.target.value || null })}
                      >
                        <option value="">— bitte wählen —</option>
                        {stamm.produkte.map((pr) => (
                          <option key={pr.id} value={pr.id}>
                            {pr.bezeichnung} ({formatE4(pr.basispreis_e4)} €/100 kg)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="feld">
                      <label>Verpackung</label>
                      <select
                        value={p.packaging_id ?? ""}
                        onChange={(e) => aendere(i, { packaging_id: e.target.value || null })}
                      >
                        <option value="">— bitte wählen —</option>
                        {stamm.verpackungen.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.bezeichnung}
                            {v.betrag_e4 !== 0 && ` (${formatE4(v.betrag_e4)} €)`}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="feld">
                      <label>Menge (t)</label>
                      <input
                        inputMode="decimal"
                        placeholder="z. B. 12,5"
                        value={p.mengeText}
                        onChange={(e) => {
                          const kg = parseTonnenToKg(e.target.value);
                          aendere(i, {
                            mengeText: e.target.value,
                            menge_kg: Number.isNaN(kg) ? 0 : kg,
                          });
                        }}
                      />
                    </div>

                    <div className="feld">
                      <label>Rabatt (%)</label>
                      <input
                        inputMode="decimal"
                        placeholder="0"
                        value={p.rabatt_bp ? String(p.rabatt_bp / 100).replace(".", ",") : ""}
                        onChange={(e) => {
                          const n = Number(e.target.value.replace(",", "."));
                          aendere(i, {
                            rabatt_bp: Number.isFinite(n)
                              ? Math.max(0, Math.min(10_000, Math.round(n * 100)))
                              : 0,
                          });
                        }}
                      />
                    </div>
                  </div>

                  {gebindeFehler && (
                    <p className="hinweistext" style={{ color: "var(--engelke-rot)" }}>
                      {verpackung!.bezeichnung} wird nur in Vielfachen von{" "}
                      {verpackung!.gebinde_kg} kg geliefert.
                    </p>
                  )}

                  <div className="feld" style={{ marginBottom: 0 }}>
                    <label>Zuschläge</label>
                    <div className="zuschlag-liste">
                      {stamm.zuschlaege.map((z) => (
                        <label key={z.id}>
                          <input
                            type="checkbox"
                            checked={p.surcharge_ids.includes(z.id)}
                            onChange={(e) =>
                              aendere(i, {
                                surcharge_ids: e.target.checked
                                  ? [...p.surcharge_ids, z.id]
                                  : p.surcharge_ids.filter((x) => x !== z.id),
                              })
                            }
                          />
                          {z.bezeichnung} <span style={{ color: "var(--faded)" }}>
                            +{formatE4(z.betrag_e4)}
                          </span>
                        </label>
                      ))}
                      <label>
                        <input
                          type="checkbox"
                          checked={p.ca_menge}
                          onChange={(e) => aendere(i, { ca_menge: e.target.checked })}
                        />
                        ca.-Menge (± 2 %)
                      </label>
                    </div>
                  </div>

                  {produkt && produkt.ust_bp !== 700 && (
                    <p className="hinweistext">
                      Dieser Artikel wird mit {formatProzent(produkt.ust_bp)} % besteuert.
                    </p>
                  )}
                </div>
              );
            })}

            <button
              className="knopf sekundaer"
              onClick={() => setPositionen((a) => [...a, leerePosition()])}
            >
              + Position hinzufügen
            </button>
          </section>

          <section className="karte">
            <div className="feld" style={{ marginBottom: 0 }}>
              <label htmlFor="bem">Bemerkung</label>
              <textarea
                id="bem"
                value={bemerkung}
                onChange={(e) => setBemerkung(e.target.value)}
                placeholder="Erscheint auf dem Angebot."
              />
            </div>
          </section>
        </div>

        {/* ── Live-Kalkulation ────────────────────────────────────────── */}
        <aside className="haftend">
          <section className="karte">
            <h2>Kalkulation</h2>

            {!vorschau && (
              <p className="hinweistext">
                Artikel und Menge wählen, dann erscheint hier die Berechnung.
              </p>
            )}

            {vorschau && (
              <div className="rechnung">
                {vorschau.ergebnis.positionen.map((e, i) => {
                  const ein = vorschau.eingaben[i];
                  return (
                    <div key={i} style={{ marginBottom: "1.1rem" }}>
                      <div style={{ fontWeight: 600, marginBottom: "0.25rem" }}>
                        {i + 1}. {ein.bezeichnung}
                      </div>
                      <div className="hinweistext" style={{ margin: "0 0 0.35rem" }}>
                        {ein.verpackung_text} · {(ein.menge_kg / 1000).toLocaleString("de-DE")} t
                      </div>

                      <div className="rechnung-zeile">
                        <span className="bez">Basispreis</span>
                        <span>{formatCent(e.basispreis_cent)}</span>
                      </div>
                      {e.verpackung_cent !== 0 && (
                        <div className="rechnung-zeile">
                          <span className="bez">Verpackung</span>
                          <span className={e.verpackung_cent < 0 ? "abschlag" : undefined}>
                            {formatCent(e.verpackung_cent)}
                          </span>
                        </div>
                      )}
                      {e.zuschlaege.map((z, j) => (
                        <div className="rechnung-zeile" key={j}>
                          <span className="bez">{z.bezeichnung}</span>
                          <span>{formatCent(z.betrag_cent)}</span>
                        </div>
                      ))}
                      {e.fracht_cent !== 0 && (
                        <div className="rechnung-zeile">
                          <span className="bez">Fracht (ab Werk)</span>
                          <span className={e.fracht_cent < 0 ? "abschlag" : undefined}>
                            {formatCent(e.fracht_cent)}
                          </span>
                        </div>
                      )}
                      <div className="rechnung-zeile summe">
                        <span className="bez">Bruttopreis €/100 kg</span>
                        <span>{formatCent(e.bruttopreis_cent)}</span>
                      </div>
                      {e.rabatt_cent !== 0 && (
                        <div className="rechnung-zeile">
                          <span className="bez">Rabatt {formatProzent(ein.rabatt_bp)} %</span>
                          <span className="abschlag">− {formatCent(e.rabatt_cent)}</span>
                        </div>
                      )}
                      <div className="rechnung-zeile summe">
                        <span className="bez">Endpreis €/100 kg</span>
                        <span>{formatCent(e.endpreis_cent)}</span>
                      </div>
                      <div className="rechnung-zeile summe">
                        <span className="bez">Positionssumme</span>
                        <span>{formatCent(e.positionssumme_cent)} €</span>
                      </div>
                    </div>
                  );
                })}

                <div className="rechnung-zeile summe">
                  <span className="bez">Summe netto</span>
                  <span>{formatCent(vorschau.ergebnis.summe_netto_cent)} €</span>
                </div>
                {vorschau.ergebnis.steuern.map((z) => (
                  <div className="rechnung-zeile" key={z.ust_bp}>
                    <span className="bez">
                      USt {formatProzent(z.ust_bp)} % auf {formatCent(z.netto_cent)}
                    </span>
                    <span>{formatCent(z.ust_cent)} €</span>
                  </div>
                ))}
                <div className="rechnung-zeile gesamt">
                  <span className="bez">Gesamt brutto</span>
                  <span>{formatCent(vorschau.ergebnis.summe_brutto_cent)} €</span>
                </div>
              </div>
            )}

            <div className="knopfzeile" style={{ marginTop: "1.2rem" }}>
              <button className="knopf" disabled={!kannSpeichern || speichert} onClick={speichern}>
                {speichert ? "Wird gespeichert …" : "Angebot speichern"}
              </button>
            </div>
            {!kundeFirma.trim() && vorschau && (
              <p className="hinweistext">Zum Speichern fehlt noch der Kunde.</p>
            )}
            {positionsFehler.map((m) => (
              <p className="hinweistext" style={{ color: "var(--engelke-rot)" }} key={m}>
                {m}
              </p>
            ))}
          </section>
        </aside>
      </div>
    </>
  );
}
